import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle, Camera, Check, Download, FileArchive, FileText, Loader2, Search as SearchIcon,
  Trash2, Upload,
} from 'lucide-react';
import { api } from '../lib/api';
import { cn, formatDate } from '../lib/utils';
import { PdfViewer, downloadFile } from '../components/PdfViewer';
import { ParsedDataTable } from '../components/ParsedDataTable';
import { MyrostarImportButton } from '../components/MyrostarImportButton';
import {
  Card, Disclosure, EmptyState, FileInput, ICON, IconButton, Notice, PageHeader, PageSkeleton, Segmented, Sheet, Skeleton, Tabs,
} from '../components/ui';
import type { DocumentRow } from '../types';
import { AfasDossier } from '../components/AfasDossier';

/* ==================================================================== */
/* Soorten en titels                                                     */
/* ==================================================================== */

const CATEGORIES: Array<{ key: string; label: string }> = [
  { key: 'loonstrook', label: 'Loonstroken' },
  { key: 'urenspec', label: 'Urenbijlagen' },
  { key: 'urencontrole', label: 'Urencontroles' },
  { key: 'rooster', label: 'Roosters' },
  { key: 'myrostar', label: 'Myrostar' },
  { key: 'contract', label: 'Contracten' },
  { key: 'contract_addendum', label: 'Addenda' },
  { key: 'cao', label: 'CAO' },
  { key: 'arbeidsvoorwaarden', label: 'Arbeidsvoorwaarden' },
  { key: 'periode_overzicht', label: 'Periodeoverzichten' },
  { key: 'bhv_basis', label: 'BHV' },
  { key: 'bhv_ploegleider', label: 'BHV ploegleider' },
  { key: 'diploma_mbo', label: "Diploma's" },
  { key: 'certificaat', label: 'Certificaten' },
  { key: 'functiewijziging', label: 'Functiewijzigingen' },
  { key: 'uren_aanpassing', label: 'Urenaanpassingen' },
  { key: 'verlofsoorten', label: 'Verlofregelingen' },
  { key: 'generatiepact', label: 'Generatiepact' },
  { key: 'loonheffing_formulier', label: 'Loonheffing' },
  { key: 'gesprek', label: 'Gesprekken' },
  { key: 'correspondentie', label: 'Correspondentie' },
  { key: 'screening', label: 'Screening' },
  { key: 'pensioenreglement', label: 'Pensioen' },
  { key: 'attentiebeleid', label: 'Attenties' },
  { key: 'jaaropgave', label: 'Jaaropgaven' },
  { key: 'overig', label: 'Overig' },
];

/** Enkelvoudige naam per soort, voor in lijsten. */
const CATEGORY_SINGULAR: Record<string, string> = {
  loonstrook: 'Loonstrook',
  urenspec: 'Urenbijlage',
  urencontrole: 'Urencontrole',
  rooster: 'Rooster',
  myrostar: 'Myrostar-rooster',
  contract: 'Contract',
  contract_addendum: 'Addendum',
  cao: 'CAO',
  arbeidsvoorwaarden: 'Arbeidsvoorwaarden',
  periode_overzicht: 'Periodeoverzicht',
  bhv_basis: 'BHV',
  bhv_ploegleider: 'BHV ploegleider',
  diploma_mbo: 'Diploma',
  certificaat: 'Certificaat',
  gesprek: 'Gesprek',
  correspondentie: 'Correspondentie',
  pensioenreglement: 'Pensioenreglement',
  attentiebeleid: 'Attentiebeleid',
  jaaropgave: 'Jaaropgave',
  uren_aanpassing: 'Urenaanpassing',
  functiewijziging: 'Functiewijziging',
  screening: 'Screening',
  verlofsoorten: 'Verlofregeling',
  generatiepact: 'Generatiepact',
  loonheffing_formulier: 'Loonheffingsformulier',
  overig: 'Overig',
};

/** Soorten die je zelf kunt toevoegen. */
const UPLOAD_CATEGORIES: Array<{ key: string; label: string }> = [
  { key: 'loonstrook', label: 'Loonstrook' },
  { key: 'urenspec', label: 'Urenbijlage' },
  { key: 'urencontrole', label: 'Urencontrole' },
  { key: 'myrostar', label: 'Myrostar-rooster' },
  { key: 'overig', label: 'Overig' },
];

function categoryLabel(key: string | null | undefined): string {
  if (!key) return 'Onbekend';
  return CATEGORY_SINGULAR[key] ?? key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
}

/** Originele bestandsnaam als titel, als die iets zegt ("Brief uren aanpassen", niet "IMG_1121"). */
function leesbareNaam(naam: string | null | undefined): string | null {
  const kaal = (naam ?? '')
    .replace(/\.[a-z0-9]{2,4}$/i, '')
    .replace(/\[[^\]]*\]/g, '')
    .replace(/[_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!kaal || /^[a-f0-9]{16,}$/i.test(kaal) || /^(img|scan|overzicht|afas-|\d)/i.test(kaal)) return null;
  return kaal.charAt(0).toUpperCase() + kaal.slice(1);
}

/** Leesbare titel: "Loonstrook periode 10 · 2026", of de naam van het stuk, in plaats van de bestandsnaam. */
function docTitle(
  d: Pick<DocumentRow, 'category' | 'period_number' | 'year' | 'document_date'> & Partial<Pick<DocumentRow, 'subcategory' | 'original_filename'>>
): string {
  const soort = categoryLabel(d.category);
  if (d.period_number) return `${soort} periode ${d.period_number}${d.year ? ` · ${d.year}` : ''}`;
  const naam = d.subcategory || leesbareNaam(d.original_filename);
  if (naam) return naam;
  if (d.document_date) return `${soort} · ${formatDate(d.document_date)}`;
  if (d.year) return `${soort} · ${d.year}`;
  return soort;
}

const fileName = (d: { renamed_filename: string | null; original_filename: string | null }) =>
  d.renamed_filename ?? d.original_filename ?? 'Naamloos bestand';

/** SQLite-tijd ("2026-10-22 02:15:00", UTC) naar ISO. */
const sqlToIso = (s: string) => (s.includes('T') ? s : `${s.replace(' ', 'T')}Z`);

function StatusLabel({ status, confidence }: { status: string; confidence: string | null }) {
  if (status === 'failed') return <span className="status-err">Inlezen mislukt</span>;
  if (status === 'pending_parse_mobile') return <span className="status-warn">Wacht op inlezen</span>;
  if (status === 'parsed' && confidence === 'low') return <span className="status-warn">Controleren</span>;
  if (status === 'parsed') return <span className="status-ok">Ingelezen</span>;
  return <span className="status-muted">Opgeslagen</span>;
}

/* ==================================================================== */
/* Pagina                                                                */
/* ==================================================================== */

/** Groepen zoals je ze zoekt: wat bij elkaar hoort staat bij elkaar. */
const GROEPEN: Array<{ key: string; label: string; soorten: string[] }> = [
  { key: 'loon', label: 'Loon', soorten: ['loonstrook', 'urenspec', 'urencontrole', 'jaaropgave', 'periode_overzicht', 'loonheffing_formulier'] },
  { key: 'rooster', label: 'Rooster', soorten: ['rooster', 'myrostar'] },
  {
    key: 'contract',
    label: 'Contract en cao',
    soorten: ['contract', 'contract_addendum', 'functiewijziging', 'uren_aanpassing', 'cao', 'arbeidsvoorwaarden', 'verlofsoorten', 'generatiepact', 'attentiebeleid', 'pensioenreglement', 'pensioen_overig'],
  },
  { key: 'opleiding', label: 'Opleiding en certificaten', soorten: ['diploma_mbo', 'certificaat', 'bhv_basis', 'bhv_ploegleider'] },
  { key: 'overig', label: 'Brieven en overig', soorten: ['correspondentie', 'screening', 'gesprek', 'overig'] },
];
const groepVan = (soort: string) => GROEPEN.find((g) => g.soorten.includes(soort))?.key ?? 'overig';

type Tab = string; // groep-key of 'afas'

export function DocumentsPage() {
  const [tab, setTab] = useState<Tab>(() => (new URLSearchParams(window.location.search).get('tab') === 'afas' ? 'afas' : 'loon'));
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [openId, setOpenId] = useState<number | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  // Alle documenten één keer ophalen (het zijn er enkele honderden); groeperen gebeurt hier.
  const { data: allDocs = [], isLoading } = useQuery<DocumentRow[]>({
    queryKey: ['documents', 'all', 'list'],
    queryFn: () => api.documents.list(),
  });
  const { data: searchDocs = [], isFetching: searching } = useQuery<DocumentRow[]>({
    queryKey: ['documents', 'search', debouncedQ],
    queryFn: () => api.documents.search(debouncedQ),
    enabled: debouncedQ !== '',
  });

  const perGroep = useMemo(() => {
    const m: Record<string, DocumentRow[]> = {};
    for (const d of allDocs) (m[groepVan(d.category)] ??= []).push(d);
    return m;
  }, [allDocs]);

  if (isLoading) return <PageSkeleton />;

  const zoekt = debouncedQ !== '';
  const groep = GROEPEN.find((g) => g.key === tab);

  return (
    <div className="page">
      <PageHeader
        title="Documenten"
        sub={`${allDocs.length} documenten, vanzelf binnengehaald uit AFAS en je mail, gelezen en op soort gezet.`}
        actions={
          <button type="button" className="btn-primary" onClick={() => setUploadOpen(true)}>
            <Upload className={ICON.sm} aria-hidden />
            Document toevoegen
          </button>
        }
      />

      <div className="relative">
        <SearchIcon className={cn(ICON.sm, 'absolute left-3 top-1/2 -translate-y-1/2 text-slate-500')} aria-hidden />
        <input
          type="search"
          placeholder="Zoek in alle documenten, bijvoorbeeld BHV, periode 9 of zomerverlof"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="input pl-9"
          aria-label="Zoeken in documenten"
        />
      </div>

      {zoekt ? (
        <section className="space-y-2">
          <h2 className="t-section">
            {searching && !searchDocs.length ? 'Zoeken…' : `${searchDocs.length} gevonden voor "${debouncedQ}"`}
          </h2>
          {searchDocs.length > 0 ? (
            <DocLijst docs={searchDocs} onOpen={setOpenId} toonSoort />
          ) : (
            !searching && (
              <Card>
                <EmptyState icon={FileText} title="Niets gevonden" text="Probeer een ander woord, of zoek op soort, zoals rooster of certificaat." />
              </Card>
            )
          )}
        </section>
      ) : (
        <>
          <Tabs
            items={[
              ...GROEPEN.map((g) => ({ value: g.key, label: g.label, count: perGroep[g.key]?.length ?? 0 })),
              { value: 'afas', label: 'AFAS-dossier' },
            ]}
            value={tab}
            onChange={setTab}
          />
          {tab === 'afas' ? (
            <AfasDossier />
          ) : tab === 'loon' ? (
            <LoonOverzicht docs={perGroep.loon ?? []} onOpen={setOpenId} />
          ) : groep ? (
            <GroepOverzicht key={groep.key} groep={groep} docs={perGroep[groep.key] ?? []} onOpen={setOpenId} onAdd={() => setUploadOpen(true)} />
          ) : null}
        </>
      )}

      <DocumentSheet id={openId} onClose={() => setOpenId(null)} />
      <UploadSheet open={uploadOpen} onClose={() => setUploadOpen(false)} />
    </div>
  );
}

/* ==================================================================== */
/* Loon: per jaar een tabel met de periodes                              */
/* ==================================================================== */

const LOON_KOLOMMEN = [
  { soort: 'loonstrook', label: 'Loonstrook' },
  { soort: 'urenspec', label: 'Urenbijlage' },
  { soort: 'urencontrole', label: 'Urencontrole' },
] as const;

function LoonOverzicht({ docs, onOpen }: { docs: DocumentRow[]; onOpen: (id: number) => void }) {
  const perPeriode = useMemo(() => {
    const m = new Map<string, Partial<Record<string, DocumentRow>>>();
    for (const d of docs) {
      if (!d.year || !d.period_number || !LOON_KOLOMMEN.some((k) => k.soort === d.category)) continue;
      const key = `${d.year}-${d.period_number}`;
      const rij = m.get(key) ?? {};
      // Bij twee versies (bv. correctie) de nieuwste tonen.
      if (!rij[d.category] || d.id > rij[d.category]!.id) rij[d.category] = d;
      m.set(key, rij);
    }
    return m;
  }, [docs]);
  const jaren = useMemo(() => [...new Set([...perPeriode.keys()].map((k) => Number(k.split('-')[0])))].sort((a, b) => b - a), [perPeriode]);
  const [jaar, setJaar] = useState<number | undefined>(undefined);
  const gekozen = jaar ?? jaren[0];
  const overig = docs.filter((d) => !LOON_KOLOMMEN.some((k) => k.soort === d.category)).sort(nieuwsteEerst);

  const periodes = Array.from({ length: 13 }, (_, i) => 13 - i).filter((p) => {
    // Alleen periodes tot en met de laatste waarvan iets binnen is (toekomst niet tonen).
    const laatste = Math.max(0, ...[...perPeriode.keys()].filter((k) => k.startsWith(`${gekozen}-`)).map((k) => Number(k.split('-')[1])));
    return p <= laatste;
  });

  return (
    <div className="space-y-4">
      <Card
        flush
        title={`Loonstukken per periode`}
        action={
          jaren.length > 1 ? (
            <Segmented label="Jaar" value={String(gekozen)} onChange={(v) => setJaar(Number(v))} items={jaren.map((j) => ({ value: String(j), label: String(j) }))} />
          ) : undefined
        }
      >
        {periodes.length ? (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Periode</th>
                  {LOON_KOLOMMEN.map((k) => (
                    <th key={k.soort}>{k.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {periodes.map((p) => {
                  const rij = perPeriode.get(`${gekozen}-${p}`) ?? {};
                  return (
                    <tr key={p}>
                      <td className="whitespace-nowrap font-medium text-slate-100">P{p}</td>
                      {LOON_KOLOMMEN.map((k) => {
                        const d = rij[k.soort];
                        return (
                          <td key={k.soort}>
                            {d ? (
                              <button
                                type="button"
                                onClick={() => onOpen(d.id)}
                                className="inline-flex items-center gap-1.5 rounded-md text-sm text-slate-100 hover:text-white hover:underline"
                              >
                                <Check className="h-3.5 w-3.5 text-money" aria-hidden />
                                Openen
                              </button>
                            ) : (
                              <span className="text-sm text-slate-500">Ontbreekt</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="px-4 pb-4 text-sm text-slate-400 sm:px-5">Nog geen loonstukken voor {gekozen}.</p>
        )}
        {gekozen && (
          <div className="border-t border-surface-line/60 px-4 py-2 sm:px-5">
            <a href={api.documents.yearZipUrl(gekozen)} className="btn-ghost btn-sm -ml-3">
              <FileArchive className={ICON.sm} aria-hidden />
              Alles van {gekozen} downloaden
            </a>
          </div>
        )}
      </Card>

      {overig.length > 0 && (
        <Card flush title="Jaaropgaven en overzichten">
          <DocLijst docs={overig} onOpen={onOpen} toonSoort zonderJaarKoppen />
        </Card>
      )}
    </div>
  );
}

/* ==================================================================== */
/* Andere groepen: filter op soort + lijst per jaar                      */
/* ==================================================================== */

function GroepOverzicht({
  groep, docs, onOpen, onAdd,
}: {
  groep: (typeof GROEPEN)[number];
  docs: DocumentRow[];
  onOpen: (id: number) => void;
  onAdd: () => void;
}) {
  const [soort, setSoort] = useState<string>('');
  const telling = useMemo(() => {
    const t: Record<string, number> = {};
    for (const d of docs) t[d.category] = (t[d.category] ?? 0) + 1;
    return t;
  }, [docs]);
  const soorten = groep.soorten.filter((s) => telling[s]);
  const zichtbaar = docs.filter((d) => !soort || d.category === soort);

  if (!docs.length) {
    return (
      <Card>
        <EmptyState icon={FileText} title={`Nog geen documenten bij ${groep.label.toLowerCase()}`} action={<button type="button" className="btn-secondary" onClick={onAdd}>Document toevoegen</button>} />
      </Card>
    );
  }
  return (
    <div className="space-y-3">
      {soorten.length > 1 && (
        <div className="flex flex-wrap gap-2" role="group" aria-label="Soort">
          {[{ s: '', label: 'Alles', n: docs.length }, ...soorten.map((s) => ({ s, label: categoryLabel(s), n: telling[s] }))].map((f) => (
            <button
              key={f.s || 'alles'}
              type="button"
              aria-pressed={soort === f.s}
              onClick={() => setSoort(f.s)}
              className={cn(
                'inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors duration-fast',
                soort === f.s ? 'border-slate-100 bg-slate-100 text-slate-900' : 'border-surface-line text-slate-300 hover:text-slate-50'
              )}
            >
              {f.label}
              <span className={cn('tabular-nums', soort === f.s ? 'text-slate-600' : 'text-slate-500')}>{f.n}</span>
            </button>
          ))}
        </div>
      )}
      <Card flush>
        <DocLijst docs={zichtbaar} onOpen={onOpen} toonSoort={!soort} perJaarMax={groep.key === 'rooster' ? 8 : undefined} />
      </Card>
    </div>
  );
}

const nieuwsteEerst = (a: DocumentRow, b: DocumentRow) =>
  (b.document_date ?? `${b.year ?? 0}`).localeCompare(a.document_date ?? `${a.year ?? 0}`) || b.id - a.id;

/** Compacte lijst, per jaar een kopje. `perJaarMax`: lange jaren eerst ingekort (roosters). */
function DocLijst({
  docs, onOpen, toonSoort, zonderJaarKoppen, perJaarMax,
}: {
  docs: DocumentRow[];
  onOpen: (id: number) => void;
  toonSoort?: boolean;
  zonderJaarKoppen?: boolean;
  perJaarMax?: number;
}) {
  const [open, setOpen] = useState<Set<string>>(new Set());
  const gesorteerd = [...docs].sort(nieuwsteEerst);
  const jaren = new Map<string, DocumentRow[]>();
  for (const d of gesorteerd) {
    const j = zonderJaarKoppen ? '' : d.year ? String(d.year) : 'Algemeen';
    jaren.set(j, [...(jaren.get(j) ?? []), d]);
  }
  return (
    <div>
      {[...jaren.entries()].map(([jaar, lijst]) => {
        const kort = perJaarMax && !open.has(jaar) ? lijst.slice(0, perJaarMax) : lijst;
        return (
          <section key={jaar || 'alles'} aria-label={jaar || undefined}>
            {jaar && (
              <h3 className="sticky top-0 z-[1] flex items-center justify-between border-b border-surface-line/60 bg-surface/95 px-4 py-2 text-xs font-medium text-slate-400 backdrop-blur sm:px-5">
                <span>{jaar}</span>
                <span className="tabular-nums">{lijst.length}</span>
              </h3>
            )}
            <div className="list">
              {kort.map((d) => (
                <DocRow key={d.id} doc={d} onOpen={onOpen} toonSoort={toonSoort} />
              ))}
            </div>
            {kort.length < lijst.length && (
              <button
                type="button"
                className="w-full border-t border-surface-line/60 px-4 py-2.5 text-left text-sm text-slate-400 hover:bg-surface-raised hover:text-slate-100 sm:px-5"
                onClick={() => setOpen((s) => new Set(s).add(jaar))}
              >
                Toon alle {lijst.length} van {jaar}
              </button>
            )}
          </section>
        );
      })}
    </div>
  );
}

function DocRow({ doc: d, onOpen, toonSoort }: { doc: DocumentRow; onOpen: (id: number) => void; toonSoort?: boolean }) {
  const name = fileName(d);
  const meta = [toonSoort && categoryLabel(d.category), d.document_date && formatDate(d.document_date)].filter(Boolean).join(', ');
  const nietKlaar = d.parse_status !== 'parsed' && d.parse_status !== 'bewaard';
  return (
    <div className="flex items-center pr-2 transition-colors duration-fast ease-calm hover:bg-surface-raised sm:pr-3">
      <button type="button" onClick={() => onOpen(d.id)} className="row min-w-0 flex-1 text-left">
        <FileText className={cn(ICON.sm, 'hidden shrink-0 text-slate-500 sm:block')} aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm text-slate-100">{docTitle(d)}</div>
          {meta && <div className="truncate text-xs text-slate-400">{meta}</div>}
        </div>
        {(nietKlaar || d.parse_confidence === 'low') && (
          <span className="shrink-0">
            <StatusLabel status={d.parse_status} confidence={d.parse_confidence} />
          </span>
        )}
      </button>
      {d.category === 'myrostar' && d.parse_status === 'parsed' && (
        <span className="hidden shrink-0 md:inline-flex">
          <MyrostarImportButton documentId={d.id} />
        </span>
      )}
      <IconButton icon={Download} label={`${name} downloaden`} onClick={() => downloadFile(api.documents.fileUrl(d.id), name)} className="shrink-0" />
    </div>
  );
}


/* ==================================================================== */
/* Detail                                                                */
/* ==================================================================== */

function DocumentSheet({ id, onClose }: { id: number | null; onClose: () => void }) {
  const qc = useQueryClient();
  const { data: doc, isLoading } = useQuery({
    queryKey: ['document', id],
    queryFn: () => api.documents.get(id!),
    enabled: id !== null,
  });

  const saveMut = useMutation({
    mutationFn: (data: any) => api.documents.patch(id!, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['document', id] });
      qc.invalidateQueries({ queryKey: ['documents'] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: () => api.documents.delete(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documents'] });
      onClose();
    },
  });

  const handleSave = async (newJson: any, note?: string) => {
    await saveMut.mutateAsync({
      parsed_json: newJson,
      parse_confidence: 'high',
      parse_note: note ?? 'manual edit via UI',
    });
  };

  const ready = id !== null && doc && doc.id === id;
  const name = ready ? fileName(doc) : null;

  return (
    <Sheet
      open={id !== null}
      onClose={onClose}
      wide
      title={ready ? docTitle(doc) : 'Document'}
      sub={ready ? <span className="block truncate" title={name ?? undefined}>{name}</span> : undefined}
      footer={
        ready ? (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <button
              type="button"
              className="btn-ghost btn-sm text-red-300 hover:text-red-200"
              disabled={deleteMut.isPending}
              onClick={() => {
                if (confirm('Dit document verwijderen?')) deleteMut.mutate();
              }}
            >
              {deleteMut.isPending ? <Loader2 className={cn(ICON.sm, 'animate-spin')} aria-hidden /> : <Trash2 className={ICON.sm} aria-hidden />}
              Verwijderen
            </button>
            <div className="flex flex-wrap items-center gap-2">
              {doc.category === 'myrostar' && doc.parse_status === 'parsed' && <MyrostarImportButton documentId={doc.id} />}
              <button type="button" className="btn-secondary btn-sm" onClick={() => downloadFile(api.documents.fileUrl(doc.id), name)}>
                <Download className={ICON.sm} aria-hidden />
                Downloaden
              </button>
            </div>
          </div>
        ) : undefined
      }
    >
      {isLoading || (id !== null && !ready && !doc) ? (
        <div className="space-y-3">
          <Skeleton className="h-[45vh] w-full" />
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-9 w-full" />
        </div>
      ) : !ready ? (
        <EmptyState icon={AlertCircle} title="Dit document bestaat niet meer" />
      ) : (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-400">
            <StatusLabel status={doc.parse_status} confidence={doc.parse_confidence} />
            <span>{categoryLabel(doc.category)}</span>
            {doc.file_size ? <span className="t-num">{(doc.file_size / 1024).toFixed(0)} kB</span> : null}
            {doc.mime_type && <span>{fileTypeLabel(doc.mime_type)}</span>}
          </div>

          {typeof doc.parsed_json?.samenvatting === 'string' && <Samenvatting info={doc.parsed_json} />}

          <PdfViewer
            src={api.documents.fileUrl(doc.id)}
            filename={name}
            className="h-[55vh] overflow-hidden rounded-lg border border-surface-line"
          />

          {typeof doc.parsed_json?.samenvatting === 'string' ? (
            <div className="-mx-4 border-t border-surface-line/60 sm:-mx-5">
              <Disclosure title="Gelezen gegevens bewerken">
                <ParsedDataTable
                  category={doc.category}
                  parsed={doc.parsed_json}
                  docConfidence={doc.parse_confidence}
                  onSave={handleSave}
                  saving={saveMut.isPending}
                />
              </Disclosure>
            </div>
          ) : (
            <ParsedDataTable
              category={doc.category}
              parsed={doc.parsed_json}
              docConfidence={doc.parse_confidence}
              onSave={handleSave}
              saving={saveMut.isPending}
            />
          )}

          {doc.parse_notes && (
            <div className="-mx-4 border-t border-surface-line/60 sm:-mx-5">
              <Disclosure title="Notities bij het inlezen" right={<span className="badge-count">{doc.parse_notes.split('\n').length}</span>}>
                <pre className="whitespace-pre-wrap break-words font-sans text-xs text-slate-300">{doc.parse_notes}</pre>
              </Disclosure>
            </div>
          )}
        </div>
      )}
    </Sheet>
  );
}

/** Wat Claude uit het document las: samenvatting, vervaldatum en de kerngegevens. */
function Samenvatting({ info }: { info: { samenvatting: string; geldig_tot?: string | null; gegevens?: Array<{ label: string; waarde: string }> } }) {
  const gegevens = info.gegevens ?? [];
  return (
    <section className="space-y-3">
      <p className="text-sm leading-relaxed text-slate-200">{info.samenvatting}</p>
      {info.geldig_tot && (
        <p className="text-sm text-slate-400">
          Geldig tot <span className="t-num text-slate-100">{formatDate(info.geldig_tot)}</span>
        </p>
      )}
      {gegevens.length > 0 && (
        <dl className="list rounded-lg border border-surface-line">
          {gegevens.map((g, i) => (
            <div key={i} className="grid gap-1 px-3 py-2 sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] sm:gap-4">
              <dt className="text-xs text-slate-400 sm:text-sm">{g.label}</dt>
              <dd className="text-sm text-slate-100">{g.waarde}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}

function fileTypeLabel(mime: string): string {
  if (mime === 'application/pdf') return 'PDF';
  if (mime.startsWith('image/')) return `Afbeelding (${mime.slice(6).toUpperCase()})`;
  return mime;
}

/* ==================================================================== */
/* Toevoegen + namen                                                     */
/* ==================================================================== */

function guessCategory(name: string): string {
  const n = name.toLowerCase();
  if (/uren.?bijlage|urenspec|10056759_\d{6}/.test(n)) return 'urenspec';
  if (/urencontrole/.test(n)) return 'urencontrole';
  if (/loonstrook|salaris/.test(n)) return 'loonstrook';
  if (/myrostar|screenshot/.test(n)) return 'myrostar';
  return 'overig';
}

type UploadItem = { file: File; status: 'wacht' | 'bezig' | 'klaar' | 'dubbel' | 'later' | 'fout'; tekst?: string };

/** Een of meer documenten toevoegen; elke PDF wordt gelezen en op de juiste plek gezet. */
function UploadSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const [items, setItems] = useState<UploadItem[]>([]);
  const [bezig, setBezig] = useState(false);
  const isMobile = typeof window !== 'undefined' && /Mobi|Android/i.test(navigator.userAgent);

  const kies = (list: FileList | null) => {
    if (!list?.length) return;
    setItems((prev) => [...prev.filter((x) => x.status === 'wacht'), ...[...list].map((file) => ({ file, status: 'wacht' as const }))]);
  };

  const start = async () => {
    setBezig(true);
    for (let i = 0; i < items.length; i++) {
      if (items[i].status !== 'wacht') continue;
      setItems((prev) => prev.map((x, j) => (j === i ? { ...x, status: 'bezig' } : x)));
      try {
        const r = await api.documents.mobileUpload(items[i].file, guessCategory(items[i].file.name));
        const status: UploadItem['status'] = r.status === 'duplicate' ? 'dubbel' : r.status === 'parsed' ? 'klaar' : 'later';
        const tekst = r.titel ? `${categoryLabel(r.categorie)}: ${r.titel}` : status === 'later' ? 'Wordt later ingelezen' : undefined;
        setItems((prev) => prev.map((x, j) => (j === i ? { ...x, status, tekst } : x)));
      } catch (e) {
        setItems((prev) => prev.map((x, j) => (j === i ? { ...x, status: 'fout', tekst: (e as Error).message } : x)));
      }
    }
    setBezig(false);
    qc.invalidateQueries({ queryKey: ['documents'] });
  };

  const close = () => {
    if (bezig) return;
    setItems([]);
    onClose();
  };

  const wachtend = items.filter((x) => x.status === 'wacht').length;
  const STATUS: Record<UploadItem['status'], { label: string; cls: string }> = {
    wacht: { label: 'Klaar om toe te voegen', cls: 'text-slate-500' },
    bezig: { label: 'Lezen…', cls: 'text-slate-300' },
    klaar: { label: 'Gelezen en gesorteerd', cls: 'text-money' },
    dubbel: { label: 'Stond er al', cls: 'text-slate-400' },
    later: { label: 'Toegevoegd', cls: 'text-slate-300' },
    fout: { label: 'Mislukt', cls: 'text-red-300' },
  };

  return (
    <Sheet
      open={open}
      onClose={close}
      title="Documenten toevoegen"
      sub="PDF's worden gelezen: de app bepaalt zelf de soort, titel en datum."
      footer={
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={close} disabled={bezig}>
            Sluiten
          </button>
          <button type="button" className="btn-primary" disabled={!wachtend || bezig} onClick={start}>
            {bezig && <Loader2 className={cn(ICON.sm, 'animate-spin')} aria-hidden />}
            {wachtend > 1 ? `${wachtend} documenten toevoegen` : 'Toevoegen'}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-secondary" onClick={() => fileRef.current?.click()} disabled={bezig}>
            <Upload className={ICON.sm} aria-hidden />
            Bestanden kiezen
          </button>
          {isMobile && (
            <button type="button" className="btn-secondary" onClick={() => cameraRef.current?.click()} disabled={bezig}>
              <Camera className={ICON.sm} aria-hidden />
              Foto maken
            </button>
          )}
          <input
            ref={fileRef}
            type="file"
            multiple
            accept="application/pdf,image/*"
            className="hidden"
            onChange={(e) => {
              kies(e.target.files);
              e.target.value = '';
            }}
          />
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => {
              kies(e.target.files);
              e.target.value = '';
            }}
          />
        </div>

        {items.length === 0 ? (
          <p className="t-muted">Kies een of meer PDF's, bijvoorbeeld alles wat je uit AFAS hebt gedownload.</p>
        ) : (
          <ul className="divide-y divide-surface-line/60 rounded-xl border border-surface-line">
            {items.map((x, i) => (
              <li key={`${x.file.name}-${i}`} className="flex items-center gap-3 px-3 py-2.5">
                {x.status === 'bezig' ? (
                  <Loader2 className={cn(ICON.sm, 'shrink-0 animate-spin text-slate-400')} aria-hidden />
                ) : (
                  <FileText className={cn(ICON.sm, 'shrink-0 text-slate-500')} aria-hidden />
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm text-slate-100">{x.file.name}</div>
                  <div className={cn('truncate text-xs', STATUS[x.status].cls)}>{x.tekst ?? STATUS[x.status].label}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Sheet>
  );
}
